import type { Ref } from 'react';
import { Bell } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useNotificationStore } from '../store/notificationStore';
import { bellLabel } from '../lib/primeNotifications';
import { useArrivalChime } from '../hooks/useArrivalChime';

/**
 * The rail's notification bell (M6 · 86d44z2b7) — replaces the sidebar's "Notifications" nav item.
 *
 * Icon-only by design now, so it carries a Tooltip AND a matching `aria-label` (UI gate §5), and
 * the label is the only place the counts reach assistive tech: the badge itself is `aria-hidden`.
 */
export function RailNotificationBell({
  open,
  onToggle,
  ref,
}: {
  open: boolean;
  onToggle: () => void;
  /** AppLayout holds this so Escape can return focus here — Radix used to do it for free. */
  ref?: Ref<HTMLButtonElement>;
}) {
  // ⚠️ Direct selectors on PRIMITIVE fields. Never a getter and never a `.filter().length` — the
  // React Compiler memoizes on the stable function reference and would serve a stale badge while
  // the numbers underneath it move (86d3d1z40).
  const needsCount = useNotificationStore((s) => s.needsCount);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const arrivalSeq = useNotificationStore((s) => s.arrivalSeq);
  const arrivalTone = useNotificationStore((s) => s.arrivalTone);

  // The audible half of the same signal. Lives here because the bell is mounted for the whole
  // session and is already the thing that owns "a notification arrived".
  useArrivalChime();

  const label = bellLabel(needsCount, unreadCount);

  // ⚠️ ALTERNATING class names, not one class toggled on and off. Re-applying the same animation
  // name to an element that is already live is a no-op — the browser sees no change and never
  // replays — so a second alert would ring nothing. Swapping A↔B is what restarts it, and the class
  // stays put between arrivals (the animations have no fill-mode, so nothing lingers visually).
  // See the `bell-arrive-*` block in globals.css; the two keyframe sets are identical on purpose.
  const arriveClass = arrivalSeq === 0 ? '' : arrivalSeq % 2 ? 'bell-arrive-a' : 'bell-arrive-b';

  // ⚠️ The ripple is COLOURED BY WHAT HAPPENED. The design drew it in Capitol Red because its demo
  // was a failure; painting a successful posting in the same red says "something is wrong" a full
  // second before the row underneath says the opposite. The tone class only re-points a CSS
  // variable — the keyframes are shared, so the A/B replay mechanism is untouched.
  const toneClass = arrivalTone ? `bell-tone-${arrivalTone}` : '';

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button
          ref={ref}
          type="button"
          aria-label={label}
          aria-pressed={open}
          onClick={onToggle}
          className={cn(
            'relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
            open
              ? 'bg-sidebar-active text-sidebar-active-foreground'
              : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground',
            arriveClass,
            toneClass
          )}
        >
          {/* Decoration only, and inert: `pointer-events-none` lives on `.bell-wave` so the rings
              can never eat the click they are drawing attention to. */}
          <span aria-hidden="true" className="bell-wave" />
          <span aria-hidden="true" className="bell-wave bell-wave-delayed" />
          <Bell className="bell-icon h-5 w-5" aria-hidden="true" />
          <NotificationBadge needsCount={needsCount} unreadCount={unreadCount} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="font-medium">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Three states, in the order a user needs them: nothing at all when caught up, a neutral dot when
 * there is merely something unread, a red count when something needs a decision.
 *
 * ⛔ Both numbers are server totals, never `items.filter(...).length` — a page is a window and a
 * badge is a total, so a user with 200 unread rows behind a 50-row page would see 50.
 */
function NotificationBadge({
  needsCount,
  unreadCount,
}: {
  needsCount: number;
  unreadCount: number;
}) {
  if (needsCount > 0) {
    return (
      <span
        aria-hidden="true"
        className="bell-badge absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-capitol-red px-1 text-2xs font-semibold text-white ring-2 ring-sidebar"
      >
        {needsCount > 9 ? '9+' : needsCount}
      </span>
    );
  }

  if (unreadCount > 0) {
    return (
      <span
        aria-hidden="true"
        className="bell-badge absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-info ring-2 ring-sidebar"
      />
    );
  }

  return null;
}

